// Admin: cross-tenant user list.
//
// On DataTable, so the summary strip, filter chips, cell typography and
// result count match every other list screen rather than being restyled here.

import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Search } from 'lucide-react';
import { PortalShell } from '../../components/shared/PortalShell';
import { StatusPill } from '../../components/shared/primitives';
import { DataTable, AvatarCell, type Column } from '../../components/shared/DataTable';
import { ADMIN_NAV } from '../_navs';
import { admin } from '../../lib/api';

type UserRow = Awaited<ReturnType<typeof admin.users>>[number];

const FILTERS = [
  { key: '', label: 'All' },
  { key: 'customer', label: 'Customers' },
  { key: 'recipient', label: 'Recipients' },
  { key: 'merchant', label: 'Merchants' },
  { key: 'admin', label: 'Admins' },
];

export function AdminUsersPage() {
  const [role, setRole] = useState('');
  const [q, setQ] = useState('');

  const usersQ = useQuery({
    queryKey: ['admin', 'users', role, q],
    queryFn: () => admin.users({ role: role || undefined, q: q.trim() || undefined }),
  });

  const rows: UserRow[] = usersQ.data ?? [];

  // Counted from the current result set, so the strip always describes what
  // is on screen rather than a separate unfiltered total.
  const summary = useMemo(() => [
    { label: 'Customers', value: String(rows.filter((u) => u.role === 'customer').length) },
    { label: 'Recipients', value: String(rows.filter((u) => u.role === 'recipient').length) },
    { label: 'Merchants', value: String(rows.filter((u) => u.role === 'merchant').length) },
    {
      label: 'Admins',
      value: String(rows.filter((u) => u.role === 'admin' || u.role === 'super_admin').length),
    },
  ], [rows]);

  const columns: Column<UserRow>[] = [
    {
      // The server resolves a person's name from their role profile into
      // `display_name`; `users` itself has no name column. Falling back to the
      // email keeps the avatar's initials meaningful for an account that has
      // no profile, rather than showing a placeholder name.
      key: 'user', header: 'Account', type: 'node', width: 'minmax(0,1.6fr)',
      value: (u) => <AvatarCell name={u.display_name ?? u.email ?? `user #${u.id}`} />,
    },
    {
      // Shown beside the name, because two people can share a name and
      // nobody shares an address.
      key: 'email', header: 'Email', type: 'text', width: 'minmax(0,1.4fr)',
      value: (u) => u.email ?? '—',
    },
    {
      key: 'contact', header: 'Phone', type: 'mono', width: 'minmax(0,1fr)',
      value: (u) => u.phone_e164 ?? '—',
    },
    {
      key: 'role', header: 'Role', type: 'text', width: '120px',
      value: (u) => u.role.replace('_', ' '),
    },
    {
      key: 'verified', header: 'Verified', type: 'muted', width: '120px',
      // Names which one is confirmed rather than a bare tick that leaves the
      // admin guessing whether email or phone is the outstanding one.
      value: (u) => {
        const bits = [u.email_verified_at && 'email', u.phone_verified_at && 'phone'].filter(Boolean);
        return bits.length ? bits.join(' + ') : 'none';
      },
    },
    {
      key: 'joined', header: 'Joined', type: 'muted', width: '110px',
      value: (u) => new Date(u.created_at).toLocaleDateString(),
    },
    {
      key: 'status', header: 'Status', type: 'node', width: '120px',
      value: (u) => <StatusPill status={u.status} />,
    },
  ];

  return (
    <PortalShell nav={ADMIN_NAV} activeId="users" title="Users" subtitle="Everyone on the platform, across every role.">
      <div className="space-y-4">
        <div className="relative max-w-md">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[var(--color-muted)]" aria-hidden="true" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Email or phone…"
            aria-label="Search users"
            className="w-full rounded-lg border border-[var(--color-border)] bg-white py-2 pl-9 pr-3 text-[13px] focus:outline-none focus:ring-2 focus:ring-brand-500/30"
          />
        </div>

        <DataTable
          title="Accounts"
          subtitle="Everyone with a login."
          summary={summary}
          filters={FILTERS}
          activeFilter={role}
          onFilter={setRole}
          columns={columns}
          rows={rows}
          rowKey={(u) => u.id}
          loading={usersQ.isLoading}
          empty={q || role ? 'Nothing matches this filter.' : 'No accounts yet.'}
        />
      </div>
    </PortalShell>
  );
}
